import { Link } from "react-router-dom";
import { ArrowLeft, Users, Target, Globe, Shield, Award, Heart, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function AboutUsPage() {
  const stats = [
    { value: "500K+", label: "Active Members" },
    { value: "$5M+", label: "Paid Out" },
    { value: "190+", label: "Countries" },
    { value: "24/7", label: "Support" }
  ];

  const values = [
    {
      icon: Shield,
      title: "Trust & Transparency",
      description: "We believe in honest communication and always pay our members on time, every time."
    },
    {
      icon: Users,
      title: "Community First",
      description: "Our members are at the heart of everything we do. Your success is our success."
    },
    {
      icon: Globe,
      title: "Global Opportunity",
      description: "We provide earning opportunities to people worldwide, regardless of location."
    },
    {
      icon: Heart,
      title: "Fair Compensation",
      description: "We ensure fair pay for all tasks and continuously work to increase earning potential."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-12 mx-auto mb-8" />
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Empowering People to Earn{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              From Anywhere
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            GigsEasy is a global rewards platform that connects people with simple online tasks, 
            helping them earn money in their spare time from the comfort of their homes.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  GigsEasy was founded with a simple mission: to create legitimate earning opportunities 
                  for people around the world. We recognized that millions of people have spare time and 
                  internet access but lack ways to monetize their time effectively.
                </p>
                <p>
                  Starting as a small platform connecting advertisers with engaged users, we've grown into 
                  a global community of over 500,000 active members across 190+ countries. Our members 
                  earn by completing simple tasks like reading emails, taking surveys, testing apps, and 
                  moderating social media.
                </p>
                <p>
                  What sets us apart is our commitment to fair pay and timely payments. We've paid out 
                  over $5 million to our members and continue to grow every day. Whether you're looking 
                  for a side income or a flexible way to earn, GigsEasy provides real opportunities 
                  without any upfront costs.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="/images/tasks-image.png" 
                alt="GigsEasy Platform"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These core principles guide everything we do at GigsEasy
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                <div className="w-14 h-14 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How We Work</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our business model is simple and transparent
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Advertisers</h3>
              <p className="text-slate-600">
                Businesses pay us to get their content, surveys, and offers in front of engaged users.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Members</h3>
              <p className="text-slate-600">
                You complete tasks, provide feedback, and engage with content - earning points for your time.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Rewards</h3>
              <p className="text-slate-600">
                Convert your points to cash via PayPal, Payoneer, or redeem for gift cards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Choose GigsEasy?</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "100% Free to Join",
              "Weekly Payments",
              "Multiple Payment Options",
              "No Hidden Fees",
              "Global Availability",
              "24/7 Support",
              "Secure Platform",
              "Verified Opportunities",
              "Fair Compensation"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <CheckCircle className="w-6 h-6 text-[#00B894] flex-shrink-0" />
                <span className="text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#00B894] to-[#0984E3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Start Earning?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join over 500,000 members who are already earning with GigsEasy. 
            It's free to join and takes less than a minute to get started.
          </p>
          <Link to="/register">
            <Button 
              className="bg-white text-[#00B894] px-10 py-6 text-lg font-medium rounded-full shadow-lg hover:bg-slate-50 hover:-translate-y-0.5 transition-all"
            >
              Create Free Account
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
